// src/utils/reporteGasConsolidado.js
// ⭐ Reportes consolidados: Por Zona y Por Delivery (como el PDF de referencia)

/**
 * ⭐ Identifica un cliente único (por cédula, si no por nombre)
 */
const claveCliente = (p) => {
  return (p.cedulaCliente && p.cedulaCliente !== '—' ? p.cedulaCliente : null)
    || p.nombreCliente
    || `id-${p.idPedidoRecarga}`;
};

/**
 * ⭐ TARIFAS DE COMISIÓN POR CILINDRO (USD por unidad)
 * El precio ya está incluido en el cilindro
 */
export const TARIFAS_COMISION = {
  10: 0.40,
  18: 0.65,
  27: 0.65,
  43: 0.70
};

/**
 * ⭐ Calcula la comisión de UN pedido según los kilos de sus cilindros
 */
export const calcularComisionPedido = (pedido) => {
  if (!pedido || !Array.isArray(pedido.detalles)) return 0;

  let comision = 0;
  for (const d of pedido.detalles) {
    const kg = Number(d.kilogramos) || 0;
    const cantidad = Number(d.cantidad) || 0;
    if (kg <= 0 || cantidad <= 0) continue;

    let tarifa = TARIFAS_COMISION[kg];
    if (tarifa === undefined) {
      const kilosDisponibles = Object.keys(TARIFAS_COMISION)
        .map(Number)
        .sort((a, b) => a - b);
      let mejor = kilosDisponibles[0];
      for (const k of kilosDisponibles) {
        if (k <= kg) mejor = k;
      }
      tarifa = TARIFAS_COMISION[mejor] || 0;
    }
    comision += tarifa * cantidad;
  }
  return comision;
};

/**
 * ⭐ Calcula la comisión total de una lista de pedidos
 */
export const calcularComisionTotal = (pedidos = []) => {
  if (!Array.isArray(pedidos)) return 0;
  return pedidos.reduce((sum, p) => sum + calcularComisionPedido(p), 0);
};

/**
 * ⭐ Cuenta cilindros por kg en una lista de pedidos
 */
export const contarCilindrosPorKg = (pedidos = [], kg) => {
  let total = 0;
  for (const p of pedidos) {
    if (!Array.isArray(p.detalles)) continue;
    for (const d of p.detalles) {
      if (Number(d.kilogramos) === Number(kg)) {
        total += Number(d.cantidad) || 0;
      }
    }
  }
  return total;
};

/**
 * ⭐ REPORTE POR ZONA
 */
export const calcularReportePorZona = (pedidos = []) => {
  if (!Array.isArray(pedidos) || pedidos.length === 0) {
    return { zonas: [], totales: { clientesUnicos: 0, totalCompras: 0, monto: 0, montoUsd: 0 } };
  }

  const mapZonas = {};

  pedidos.forEach((p) => {
    const zona = p.nombreZona || 'Sin zona';

    if (!mapZonas[zona]) {
      mapZonas[zona] = {
        zona,
        clientes: new Set(),
        compras: 0,
        montoBs: 0,
        montoUsd: 0,
        kilos: 0,
        litros: 0,
        cantidad: 0
      };
    }

    const z = mapZonas[zona];
    z.clientes.add(claveCliente(p));
    z.compras += 1;
    z.montoBs += Number(p.totalBs || 0);
    z.montoUsd += Number(p.totalUsd || 0);
    z.kilos += Number(p.kilosTotales || 0);
    z.litros += Number(p.litrosTotales || 0);
    z.cantidad += Number(p.cantidadTotal || 0);
  });

  const zonas = Object.values(mapZonas).map((z) => ({
    zona: z.zona,
    clientesUnicos: z.clientes.size,
    compras: z.compras,
    montoBs: z.montoBs,
    montoUsd: z.montoUsd,
    kilos: z.kilos,
    litros: z.litros,
    cantidad: z.cantidad
  }));

  const totales = {
    clientesUnicos: zonas.reduce((s, z) => s + z.clientesUnicos, 0),
    totalCompras: zonas.reduce((s, z) => s + z.compras, 0),
    montoBs: zonas.reduce((s, z) => s + z.montoBs, 0),
    montoUsd: zonas.reduce((s, z) => s + z.montoUsd, 0),
    kilos: zonas.reduce((s, z) => s + z.kilos, 0),
    litros: zonas.reduce((s, z) => s + z.litros, 0),
    cantidad: zonas.reduce((s, z) => s + z.cantidad, 0)
  };

  zonas.sort((a, b) => b.montoBs - a.montoBs);

  zonas.forEach((z) => {
    z.pctClientes = totales.clientesUnicos > 0
      ? (z.clientesUnicos / totales.clientesUnicos) * 100
      : 0;
    z.pctCompras = totales.totalCompras > 0
      ? (z.compras / totales.totalCompras) * 100
      : 0;
    z.pctMonto = totales.montoBs > 0
      ? (z.montoBs / totales.montoBs) * 100
      : 0;
    z.ticketPromedio = z.clientesUnicos > 0
      ? z.montoBs / z.clientesUnicos
      : 0;
  });

  return { zonas, totales };
};

/**
 * ⭐ REPORTE POR DELIVERY
 * Incluye comisión por delivery, por zona y por pedido.
 */
export const calcularReportePorDelivery = (pedidos = [], asignaciones = [], deliveries = []) => {
  if (!Array.isArray(pedidos) || pedidos.length === 0) {
    return { deliveries: [], totales: { pedidos: 0, clientesUnicos: 0, montoBs: 0, montoUsd: 0, comision: 0 } };
  }

  const asigPorPedido = {};
  (asignaciones || []).forEach((a) => {
    const idPedido = a.idPedidoRecarga ?? a.idPedido ?? a.pedidoId;
    if (idPedido) {
      if (!asigPorPedido[idPedido]) asigPorPedido[idPedido] = a;
    }
  });

  const delivPorId = {};
  (deliveries || []).forEach((d) => {
    const id = d.idDelivery ?? d.id;
    if (id) delivPorId[id] = d;
  });

  const mapDeliveries = {};

  pedidos.forEach((p) => {
    const idPedido = p.idPedidoRecarga ?? p.idPedido;
    const asig = asigPorPedido[idPedido];
    if (!asig) return;

    const idDeliv = asig.idDelivery ?? asig.deliveryId ?? asig.idDeliveryFK;
    if (!idDeliv) return;

    const deliv = delivPorId[idDeliv] || {};
    const nombre = deliv.nombre || deliv.nombreCompleto || asig.nombreDelivery
      || `Delivery #${idDeliv}`;

    if (!mapDeliveries[idDeliv]) {
      mapDeliveries[idDeliv] = {
        idDelivery: idDeliv,
        nombre,
        pedidos: 0,
        clientes: new Set(),
        zonas: new Set(),
        montoBs: 0,
        montoUsd: 0,
        kilos: 0,
        litros: 0,
        cantidad: 0,
        comision: 0,
        cilindrosPorKg: { 10: 0, 18: 0, 27: 0, 43: 0 },
        zonasDetalle: {},
        pedidosDetalle: []
      };
    }

    const d = mapDeliveries[idDeliv];
    d.pedidos += 1;
    d.clientes.add(claveCliente(p));
    d.montoBs += Number(p.totalBs || 0);
    d.montoUsd += Number(p.totalUsd || 0);
    d.kilos += Number(p.kilosTotales || 0);
    d.litros += Number(p.litrosTotales || 0);
    d.cantidad += Number(p.cantidadTotal || 0);

    const comisionPedido = calcularComisionPedido(p);
    d.comision += comisionPedido;
    d.pedidosDetalle.push({
      idPedido,
      numeroPedido: p.numeroPedido,
      cliente: p.nombreCliente,
      zona: p.nombreZona,
      totalUsd: Number(p.totalUsd || 0),
      totalBs: Number(p.totalBs || 0),
      comision: comisionPedido
    });

    if (Array.isArray(p.detalles)) {
      for (const det of p.detalles) {
        const kg = Number(det.kilogramos);
        const cant = Number(det.cantidad) || 0;
        if (d.cilindrosPorKg[kg] !== undefined) {
          d.cilindrosPorKg[kg] += cant;
        }
      }
    }

    const zona = p.nombreZona || 'Sin zona';
    d.zonas.add(zona);

    if (!d.zonasDetalle[zona]) {
      d.zonasDetalle[zona] = {
        zona,
        pedidos: 0,
        clientes: new Set(),
        montoBs: 0,
        montoUsd: 0,
        comision: 0
      };
    }
    const zd = d.zonasDetalle[zona];
    zd.pedidos += 1;
    zd.clientes.add(claveCliente(p));
    zd.montoBs += Number(p.totalBs || 0);
    zd.montoUsd += Number(p.totalUsd || 0);
    zd.comision += comisionPedido;
  });

  const lista = Object.values(mapDeliveries).map((d) => ({
    idDelivery: d.idDelivery,
    nombre: d.nombre,
    pedidos: d.pedidos,
    clientesUnicos: d.clientes.size,
    zonasCount: d.zonas.size,
    zonas: Array.from(d.zonas).sort(),
    montoBs: d.montoBs,
    montoUsd: d.montoUsd,
    kilos: d.kilos,
    litros: d.litros,
    cantidad: d.cantidad,
    comision: d.comision,
    cilindrosPorKg: d.cilindrosPorKg,
    ticketPromedio: d.clientes.size > 0 ? d.montoBs / d.clientes.size : 0,
    pedidosDetalle: d.pedidosDetalle,
    zonasDetalle: Object.values(d.zonasDetalle).map((z) => ({
      zona: z.zona,
      pedidos: z.pedidos,
      clientesUnicos: z.clientes.size,
      montoBs: z.montoBs,
      montoUsd: z.montoUsd,
      comision: z.comision
    })).sort((a, b) => b.montoBs - a.montoBs)
  }));

  const totales = {
    pedidos: lista.reduce((s, d) => s + d.pedidos, 0),
    clientesUnicos: lista.reduce((s, d) => s + d.clientesUnicos, 0),
    montoBs: lista.reduce((s, d) => s + d.montoBs, 0),
    montoUsd: lista.reduce((s, d) => s + d.montoUsd, 0),
    kilos: lista.reduce((s, d) => s + d.kilos, 0),
    litros: lista.reduce((s, d) => s + d.litros, 0),
    cantidad: lista.reduce((s, d) => s + d.cantidad, 0),
    comision: lista.reduce((s, d) => s + d.comision, 0),
    cilindrosPorKg: {
      10: lista.reduce((s, d) => s + (d.cilindrosPorKg[10] || 0), 0),
      18: lista.reduce((s, d) => s + (d.cilindrosPorKg[18] || 0), 0),
      27: lista.reduce((s, d) => s + (d.cilindrosPorKg[27] || 0), 0),
      43: lista.reduce((s, d) => s + (d.cilindrosPorKg[43] || 0), 0)
    }
  };

  lista.sort((a, b) => b.montoBs - a.montoBs);

  lista.forEach((d) => {
    d.pctMonto = totales.montoBs > 0 ? (d.montoBs / totales.montoBs) * 100 : 0;
    d.pctPedidos = totales.pedidos > 0 ? (d.pedidos / totales.pedidos) * 100 : 0;
    d.pctClientes = totales.clientesUnicos > 0 ? (d.clientesUnicos / totales.clientesUnicos) * 100 : 0;
  });

  return { deliveries: lista, totales };
};

/**
 * ⭐ RESUMEN EJECUTIVO
 */
export const calcularResumenEjecutivo = (pedidos = [], asignaciones = [], deliveries = []) => {
  const porZona = calcularReportePorZona(pedidos);
  const porDelivery = calcularReportePorDelivery(pedidos, asignaciones, deliveries);

  const zonaPrincipal = porZona.zonas[0] || null;
  const liderComercial = porDelivery.deliveries[0] || null;

  return {
    montoConsumidoBs: porZona.totales.montoBs,
    montoConsumidoUsd: porZona.totales.montoUsd,
    clientesUnicos: porZona.totales.clientesUnicos,
    totalCompras: porZona.totales.totalCompras,
    totalPedidos: pedidos.length,
    comisionTotal: porDelivery.totales.comision,
    zonaPrincipal: zonaPrincipal
      ? {
        nombre: zonaPrincipal.zona,
        montoBs: zonaPrincipal.montoBs,
        pct: zonaPrincipal.pctMonto
      }
      : null,
    liderComercial: liderComercial
      ? {
        nombre: liderComercial.nombre,
        montoBs: liderComercial.montoBs,
        pct: liderComercial.pctMonto,
        pedidos: liderComercial.pedidos,
        zonasCount: liderComercial.zonasCount
      }
      : null
  };
};

/**
 * ⭐ FORMATEAR NÚMERO para reportes (formato: 1.839,00)
 */
export const fmt = (n, dec = 2) => {
  if (n === null || n === undefined) return dec === 0 ? '0' : '0,00';
  const num = Number(n);
  if (isNaN(num)) return dec === 0 ? '0' : '0,00';
  return num.toLocaleString('es-VE', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec
  });
};

export const fmtBs = (n) => `Bs. ${fmt(n, 2)}`;
export const fmtUsd = (n) => `$${fmt(n, 2)}`;
export const fmtPct = (n) => `${fmt(n, 2)}%`;